import React, { Fragment, useState } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import {
  Button,
  ButtonGroup,
  Container,
  Grid,
  Paper,
  Typography,
  Chip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { PickingDiff } from '../../../interfaces/PickingValidate/PickingDiff'
import { ListElementText } from '../../UI/ListElementText'
import { useStyles } from './styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { eTypePickingRoute } from '../../../enums/eTypePickingRoute'
import { eTypePickingRouteMovement } from '../../../enums/eTypePickingRouteMovementId'
import { DateTime } from 'luxon'

interface PickingDiffElementProps {
  element: PickingDiff
  totals: PickingDiff
  handleUpdateElement: (element: PickingDiff) => void
  typePickingRouteId: number
  orderCancellationRequest: boolean
}

export const PickingDiffElement: React.FC<PickingDiffElementProps> = ({
  element,
  totals,
  handleUpdateElement,
  typePickingRouteId,
  orderCancellationRequest
}) => {
  const classes = useStyles()
  const [error, setError] = useState<string>('')

  const clearError = () => {
    setError('')
  }

  const handlePickingToStock = () => {
    clearError()
    if (element.adjustedPickedQty() <= 0) {
      setError('La cantidad pickeada es cero')
      return
    }
    element.pickedAdjustmentQty -= 1
    element.stockAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const handleStockToPicking = () => {
    clearError()
    if (element.adjustedStockQty() <= 0) {
      setError('No hay existencias en la posición')
      return
    }
    if (totals.pendingQty() <= 0) {
      setError('Total de ruta completado')
      return
    }
    element.stockAdjustmentQty -= 1
    element.pickedAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const handleStockToShortages = () => {
    clearError()
    if (element.adjustedStockQty() <= 0) {
      setError('Sin existencias en la posición')
      return
    }
    element.stockAdjustmentQty -= 1
    element.differenceQty += 1
    handleUpdateElement(element)
  }

  const handleShortagesToStock = () => {
    clearError()
    if (element.differenceQty <= 0) {
      setError('No existen diferencias/mermas reportadas')
      return
    }
    element.differenceQty -= 1
    element.stockAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const handleStockToQuality = () => {
    clearError()
    if (element.adjustedStockQty() <= 0) {
      setError('Sin existencias en la posición')
      return
    }
    element.stockAdjustmentQty -= 1
    element.reductionQty += 1
    handleUpdateElement(element)
  }

  const handleQualityToStock = () => {
    clearError()
    if (element.reductionQty <= 0) {
      setError('No existen unidades en calidad reportadas')
      return
    }
    element.reductionQty -= 1
    element.stockAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const handlePickingToShortages = () => {
    clearError()
    if (element.adjustedPickedQty() <= 0) {
      setError('La cantidad pickeada es cero')
      return
    }
    element.pickedAdjustmentQty -= 1
    element.differenceQty += 1
    handleUpdateElement(element)
  }

  const handleShortagesToPicking = () => {
    clearError()
    if (element.differenceQty <= 0) {
      setError('No hay existencias en la posición')
      return
    }
    if (totals.pendingQty() <= 0) {
      setError('Total de ruta completado')
      return
    }
    element.differenceQty -= 1
    element.pickedAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const handlePickingToQuality = () => {
    clearError()
    if (element.adjustedPickedQty() <= 0) {
      setError('Sin existencias en la posición')
      return
    }
    element.pickedAdjustmentQty -= 1
    element.reductionQty += 1
    handleUpdateElement(element)
  }

  const handleQualityToPicking = () => {
    clearError()
    if (element.reductionQty <= 0) {
      setError('No existen unidades en calidad reportadas')
      return
    }
    if (totals.pendingQty() <= 0) {
      setError('Total de ruta completado')
      return
    }
    element.reductionQty -= 1
    element.pickedAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const handlePickingToReserve = () => {
    clearError()
    if (element.adjustedPickedQty() <= 0) {
      setError('Sin existencias en la posición')
      return
    }
    element.pickedAdjustmentQty -= 1
    element.reservedQty += 1
    handleUpdateElement(element)
  }

  const handleReserveToPicking = () => {
    clearError()
    if (element.reservedQty <= 0) {
      setError('No existen unidades en calidad reportadas')
      return
    }
    if (totals.pendingQty() <= 0) {
      setError('Total de ruta completado')
      return
    }
    element.reservedQty -= 1
    element.pickedAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const formatDate = (dateString: string) => {
    try {
      return DateTime.fromISO(dateString).toFormat('dd/MM/yyyy')
    } catch {
      return 'Fecha inválida'
    }
  }

  const renderBatchInfo = () => {
    if (!element.batch) {
      return null // No mostrar nada si no hay batch
    }

    const hasBatchNumber =
      element.batch.batchNumber && element.batch.batchNumber.trim() !== ''
    const hasExpirationDate = element.batch.expirationDate

    const chips = []

    if (hasBatchNumber) {
      chips.push(
        <Chip
          key="batch"
          size='small'
          label={`Lote: ${element.batch.batchNumber}`}
          variant='outlined'
          style={{ backgroundColor: '#e3f2fd', color: '#1976d2', marginRight: '8px' }}
        />
      )
    }

    if (hasExpirationDate) {
      chips.push(
        <Chip
          key="expiration"
          size='small'
          label={`Venc: ${formatDate(element.batch.expirationDate)}`}
          variant='outlined'
          style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
        />
      )
    }

    if (chips.length === 0) {
      return null // No hay información útil, no mostrar nada
    }

    return <div style={{ display: 'flex', alignItems: 'center' }}>{chips}</div>
  }

  const handlePickingToCommercialRejection = () => {
    clearError()
    if (element.adjustedPickedQty() <= 0) {
      setError('Sin existencias en la posición')
      return
    }
    element.pickedAdjustmentQty -= 1
    element.commercialRejectiontQty += 1
    handleUpdateElement(element)
  }

  const handleCommercialRejectionToPicking = () => {
    clearError()
    if (element.commercialRejectiontQty <= 0) {
      setError('No existen unidades en rechazo comercial')
      return
    }
    element.commercialRejectiontQty -= 1
    element.pickedAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const handleStockToCommercialRejection = () => {
    clearError()
    if (element.adjustedStockQty() <= 0) {
      setError('Sin existencias en la posición')
      return
    }
    element.stockAdjustmentQty -= 1
    element.commercialRejectiontQty += 1
    handleUpdateElement(element)
  }

  const handleCommercialRejectionToStock = () => {
    clearError()
    if (element.commercialRejectiontQty <= 0) {
      setError('No existen unidades en rechazo comercial')
      return
    }
    element.commercialRejectiontQty -= 1
    element.stockAdjustmentQty += 1
    handleUpdateElement(element)
  }

  const getPaperStyle = () => {
    const baseStyle = { padding: '10px', margin: '3px' }
    if (element.batch) {
      return {
        ...baseStyle,
        borderLeft: '4px solid #1976d2',
        backgroundColor: '#fafafa'
      }
    }
    return baseStyle
  }

  return typePickingRouteId === eTypePickingRoute.ExternalStorage &&
    element.typePickingRouteMovementId ===
      eTypePickingRouteMovement.PickingRoute ? (
    <Fragment>
      <Paper elevation={3} style={getPaperStyle()}>
        <Grid container justify='center' alignItems='center'>
          <Grid item container xs={8}>
            <Grid item xs={12} sm={4}>
              <ListElementText title='POSICIÓN' text={element.positionName} />
              <Typography
                className={`${classes.listelementtext}`}
                variant='body2'
                component='p'
              >
                {element.isRoutePosition() ? 'RUTA PICKING' : 'SIN RUTA'}
              </Typography>
              <div style={{ marginTop: '8px' }}>{renderBatchInfo()}</div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <ListElementText title='PICKING' text={element.pickedLabel()} />
            </Grid>
          </Grid>
          <Grid item container xs={4} direction='row' spacing={2}>
            <Grid item xs={12} sm={6}>
              <ButtonGroup
                orientation='vertical'
                aria-label='vertical contained primary button group'
                variant='contained'
              >
                <Button
                  color='primary'
                  size='small'
                  onClick={handlePickingToShortages}
                  startIcon={<ArrowForwardIcon />}
                />
                <Button
                  className={classes.redButton}
                  size='small'
                  onClick={handleShortagesToPicking}
                  startIcon={<ArrowBackIcon />}
                />
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListElementText
                title='DECLARAR FALTANTE'
                text={element.differenceLabel()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ButtonGroup
                orientation='vertical'
                aria-label='vertical contained primary button group'
                variant='contained'
              >
                <Button
                  color='primary'
                  size='small'
                  onClick={handlePickingToQuality}
                  startIcon={<ArrowForwardIcon />}
                />
                <Button
                  className={classes.redButton}
                  size='small'
                  onClick={handleQualityToPicking}
                  startIcon={<ArrowBackIcon />}
                />
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListElementText
                title='ENVIAR A CALIDAD'
                text={element.reductionLabel()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ButtonGroup
                orientation='vertical'
                aria-label='vertical contained primary button group'
                variant='contained'
              >
                <Button
                  color='primary'
                  size='small'
                  onClick={handlePickingToCommercialRejection}
                  startIcon={<ArrowForwardIcon />}
                />
                <Button
                  className={classes.redButton}
                  size='small'
                  onClick={handleCommercialRejectionToPicking}
                  startIcon={<ArrowBackIcon />}
                />
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListElementText
                title='RECHAZO COMERCIAL'
                text={element.commercialRejectionLabel()}
              />
            </Grid>
            {orderCancellationRequest && (
              <Fragment>
                <Grid item xs={12} sm={6}>
                  <ButtonGroup
                    orientation='vertical'
                    aria-label='vertical contained primary button group'
                    variant='contained'
                  >
                    <Button
                      color='primary'
                      size='small'
                      onClick={handlePickingToReserve}
                      startIcon={<ArrowForwardIcon />}
                    />
                    <Button
                      className={classes.redButton}
                      size='small'
                      onClick={handleReserveToPicking}
                      startIcon={<ArrowBackIcon />}
                    />
                  </ButtonGroup>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ListElementText
                    title='ENVIAR A RESERVA'
                    text={element.reservedLabel()}
                  />
                </Grid>
              </Fragment>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Container maxWidth='xl'>
        <div>
          {error !== '' && (
            <Card className={classes.root}>
              <div className={classes.errorcard}>
                <CardContent className={classes.content}>
                  <Grid container direction='row' spacing={2}>
                    <Grid item xs={12} sm={4} lg={4}>
                      <ul>
                        <li>
                          <span style={{ color: 'red' }}>{error}</span>
                        </li>
                      </ul>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={4}>
                      <Button
                        size='small'
                        variant='contained'
                        color='inherit'
                        startIcon={<CloseIcon />}
                        onClick={() => clearError()}
                      >
                        Cerrar
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </div>
            </Card>
          )}
        </div>
      </Container>
    </Fragment>
  ) : (
    <Fragment>
      <Paper elevation={3} style={getPaperStyle()}>
        <Grid container justify='center' alignItems='center'>
          <Grid item container xs={8}>
            <Grid item xs={12} sm={3}>
              <ListElementText title='POSICIÓN' text={element.positionName} />
              <Typography
                className={`${classes.listelementtext}`}
                variant='body2'
                component='p'
              >
                {element.isRoutePosition() ? 'RUTA PICKING' : 'SIN RUTA'}
              </Typography>
              <div style={{ marginTop: '8px' }}>{renderBatchInfo()}</div>
            </Grid>
            <Grid item xs={12} sm={3}>
              <ListElementText title='PICKING' text={element.pickedLabel()} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ButtonGroup
                orientation='vertical'
                aria-label='vertical contained primary button group'
                variant='contained'
              >
                <Button
                  color='primary'
                  size='small'
                  onClick={handlePickingToStock}
                  startIcon={<ArrowForwardIcon />}
                />
                <Button
                  className={classes.redButton}
                  size='small'
                  onClick={handleStockToPicking}
                  startIcon={<ArrowBackIcon />}
                />
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} sm={3}>
              <ListElementText title='DISPONIBLE' text={element.stockLabel()} />
            </Grid>
          </Grid>
          <Grid item container xs={4} direction='row' spacing={2}>
            <Grid item xs={12} sm={6}>
              <ButtonGroup
                orientation='vertical'
                aria-label='vertical contained primary button group'
                variant='contained'
              >
                <Button
                  color='primary'
                  size='small'
                  onClick={handleStockToShortages}
                  startIcon={<ArrowForwardIcon />}
                />
                <Button
                  className={classes.redButton}
                  size='small'
                  onClick={handleShortagesToStock}
                  startIcon={<ArrowBackIcon />}
                />
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListElementText
                title='DECLARAR FALTANTE'
                text={element.differenceLabel()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ButtonGroup
                orientation='vertical'
                aria-label='vertical contained primary button group'
                variant='contained'
              >
                <Button
                  color='primary'
                  size='small'
                  onClick={handleStockToQuality}
                  startIcon={<ArrowForwardIcon />}
                />
                <Button
                  className={classes.redButton}
                  size='small'
                  onClick={handleQualityToStock}
                  startIcon={<ArrowBackIcon />}
                />
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListElementText
                title='ENVIAR A CALIDAD'
                text={element.reductionLabel()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ButtonGroup
                orientation='vertical'
                aria-label='vertical contained primary button group'
                variant='contained'
              >
                <Button
                  color='primary'
                  size='small'
                  onClick={handleStockToCommercialRejection}
                  startIcon={<ArrowForwardIcon />}
                />
                <Button
                  className={classes.redButton}
                  size='small'
                  onClick={handleCommercialRejectionToStock}
                  startIcon={<ArrowBackIcon />}
                />
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListElementText
                title='RECHAZO COMERCIAL'
                text={element.commercialRejectionLabel()}
              />
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Container maxWidth='xl'>
        <div>
          {error !== '' && (
            <Card className={classes.root}>
              <div className={classes.errorcard}>
                <CardContent className={classes.content}>
                  <Grid container direction='row' spacing={2}>
                    <Grid item xs={12} sm={4} lg={4}>
                      <ul>
                        <li>
                          <span style={{ color: 'red' }}>{error}</span>
                        </li>
                      </ul>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={4}>
                      <Button
                        size='small'
                        variant='contained'
                        color='inherit'
                        startIcon={<CloseIcon />}
                        onClick={() => clearError()}
                      >
                        Cerrar
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </div>
            </Card>
          )}
        </div>
      </Container>
    </Fragment>
  )
}
